<template>
  <div class="control-pane control-pane-coordinate-panel d-none" id="coordinatePanel">
    <div v-if="(geolocateActive && geolocateInfo) || defaultElevationMode != ElevationMode.Disabled" id="coordPanelGeolocate" class="small text-muted mb-2">
      <div v-if="geolocateActive && geolocateInfo" class="border-bottom border-1 border-secondary-subtle">
        <div class="d-inline-block" v-bind:style="'transform: rotate(' + (geolocateInfo.bearing - 90) + 'deg)'">&#10148;</div>
        <span class="ms-1">{{ geolocateInfo.formattedDistance }},</span>
        <span class="ms-1">{{ geolocateInfo.formattedBearing }}</span>
        <span class="ms-1">({{ geolocateInfo.formattedCardinalDirection }})</span>
      </div>
      <div v-if="defaultElevationMode != ElevationMode.Disabled" class="d-inline-block border-bottom border-1 border-secondary-subtle">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" stroke="currentColor" class="bi custom-icon-mountains" viewBox="0 0 16 16">
          <path
              style="fill:none;stroke-width:1;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:none;stroke-opacity:1"
              d="M 0.5,15.5 6,0.5 10,11 m -1,-3 2.5,-4 4,9"
          />
        </svg>
        <span class="ms-1" v-if="elevation">{{ elevation.formattedElevation }}</span>
        <span class="ms-1" v-else-if="awaitingElevationPromise > 0">
          <span class="lds-dual-ring"></span>
        </span>
        <span class="ms-1 cursor-pointer" v-else v-on:click="enableElevation">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-toggle-off" viewBox="0 0 16 16">
            <path d="M11 4a4 4 0 0 1 0 8H8a5 5 0 0 0 2-4 5 5 0 0 0-2-4zm-6 8a4 4 0 1 1 0-8 4 4 0 0 1 0 8M0 8a5 5 0 0 0 5 5h6a5 5 0 0 0 0-10H5a5 5 0 0 0-5 5"/>
          </svg>
        </span>
      </div>
      <div class="d-inline-block border-bottom border-1 border-secondary-subtle ms-2">
        <svg width="16" height="16" fill="currentColor" class="bi bi-compass-declination" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
          <path style="fill: none;stroke:currentColor;stroke-width:0.7;stroke-linecap:butt" d="M 7.9984071,3.6709105 A 4.4887352,4.4887352 0 0 1 10.975943,4.8102074" />
          <path d="M8 16.016a7.5 7.5 0 0 0 1.962-14.74A1 1 0 0 0 9 0H7a1 1 0 0 0-.962 1.276A7.5 7.5 0 0 0 8 16.016m6.5-7.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0" />
          <path d="m6.94 7.44 4.95-2.83-2.83 4.95-4.949 2.83 2.828-4.95z" />
        </svg>

        <span
            class="ms-1"
            v-if="magneticNorthData"
            v-bind:title="'Declinatie: ' + formatDeltaDegrees(magneticNorthData.dec, 2) + ', Inclinatie: ' + formatDeltaDegrees(magneticNorthData.dip, 2)"
        >{{ formatDeltaDegrees(magneticNorthData.dec) }}</span>
      </div>
    </div>

    <button type="button" class="btn-close" aria-label="Close" v-on:click="closeCoordinatePanel" title="Sluiten"></button>

    <button type="button" class="btn-close open-sketch-route" aria-label="Close" v-on:click="openSketchRoute" title="Afstand meten">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class=" bi bi-rulers" viewBox="0 0 16 16">
        <path d="M1 0a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h5v-1H2v-1h4v-1H4v-1h2v-1H2v-1h4V9H4V8h2V7H2V6h4V2h1v4h1V4h1v2h1V2h1v4h1V4h1v2h1V2h1v4h1V1a1 1 0 0 0-1-1H1z"/>
      </svg>
    </button>

    <button type="button" class="btn-close make-location-from-coordinate" aria-label="Close" v-on:click="makeLocationFromCoordinate" title="Maak punt" v-if="!userInterface.isLocked()">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-plus-circle" viewBox="0 0 16 16">
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
      </svg>
    </button>

    <div class="dropup dropup-center">
      <button type="button" class="btn-close open-coordinate-links" data-bs-toggle="dropdown" aria-expanded="false" title="Links">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-link-45deg" viewBox="2 2 14 14">
          <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z"/>
          <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243z"/>
        </svg>
      </button>
      <ul class="dropdown-menu">
        <li>
          <div class="dropdown-item coordinate-link-item" v-for="coordinateLink in coordinateLinks">
            <a :href="coordinateLink.url" target="_blank">{{ coordinateLink.label }}</a>

            <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                v-on:click="copyCoordinateLink(coordinateLink)"
                title="Kopiëren"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="align-baseline bi bi-clipboard-plus" viewBox="0 0 16 16">
                <path fill-rule="evenodd" d="M8 7a.5.5 0 0 1 .5.5V9H10a.5.5 0 0 1 0 1H8.5v1.5a.5.5 0 0 1-1 0V10H6a.5.5 0 0 1 0-1h1.5V7.5A.5.5 0 0 1 8 7z"/>
                <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
              </svg>
            </button>
          </div>
        </li>
      </ul>
    </div>

    <div class="control-pane-content mt-4" id="coordinateControlPanelContent">
      <div v-for="coordinateInSystem in coordinateInSystems" :key="coordinateInSystem.id">
        <div class="mt-1">
          <div class="input-group dropup">
            <label
                class="input-group-text coordinate-short-name-label"
                v-bind:for="'coord_panel_input_' + coordinateInSystem.id"
            >
              <span class="mx-auto small">{{ coordinateInSystem.short_name }}</span>
            </label>

            <input
                type="text"
                class="form-control form-control-sm"
                v-bind:id="'coord_panel_input_' + coordinateInSystem.id"
                readonly
                v-bind:value="coordinateInSystem.showFormatted"
                aria-label="Coordinate"
            >

            <button
                type="button"
                class="btn btn-outline-secondary"
                v-on:click="copyInput('#coord_panel_input_' + coordinateInSystem.id)"
                title="Kopiëren"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="align-baseline bi bi-clipboard-plus" viewBox="0 0 16 16">
                <path fill-rule="evenodd" d="M8 7a.5.5 0 0 1 .5.5V9H10a.5.5 0 0 1 0 1H8.5v1.5a.5.5 0 0 1-1 0V10H6a.5.5 0 0 1 0-1h1.5V7.5A.5.5 0 0 1 8 7z"/>
                <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
              </svg>
            </button>
            <button
                class="btn btn-outline-secondary dropdown-toggle"
                type="button" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
                v-on:click="updateFormatDropdownWidth"
                title="Formaten"
            ></button>
            <div class="dropdown-menu dropdown-menu-end">
              <div class="mx-2">
                <label class="form-label">{{ coordinateInSystem.name }}</label>

                <div v-for="(coordinate, formatName) in coordinateInSystem.formatted" :key="formatName">
                  <div class="input-group mb-1">
                    <input
                        type="text"
                        class="form-control"
                        v-bind:id="'coord_panel_input_' + coordinateInSystem.id + '_' + formatName"
                        readonly
                        v-bind:value="coordinate"
                        aria-label="Coordinate"
                    >

                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        v-on:click="copyInput('#coord_panel_input_' + coordinateInSystem.id + '_' + formatName)"
                        title="Kopiëren"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="align-baseline bi bi-clipboard-plus" viewBox="0 0 16 16">
                        <path fill-rule="evenodd" d="M8 7a.5.5 0 0 1 .5.5V9H10a.5.5 0 0 1 0 1H8.5v1.5a.5.5 0 0 1-1 0V10H6a.5.5 0 0 1 0-1h1.5V7.5A.5.5 0 0 1 8 7z"/>
                        <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                        <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div v-if="coordinateInSystem.id in northGraphicResults" class="d-flex flex-row" @click.stop>
                  <div class="flex-fill"></div>

                  <div v-html="northGraphicResults[coordinateInSystem.id].svg_string" class="me-2"></div>

                  <div :class="isMobile() ? 'size70' : 'small'">
                    <div class="fw-bold">Magnetische declinatie</div>
                    <div class="mb-2">a = MN&minus;WN = {{ northGraphicResults[coordinateInSystem.id].magneticDeclinationFormatted }}</div>

                    <div class="fw-bold">Miswijzing</div>
                    <div class="mb-2">b = MN&minus;GN = {{ northGraphicResults[coordinateInSystem.id].gridDeclinationFormatted }}</div>

                    <div class="fw-bold">Meridiaanconvergentie</div>
                    <div class="mb-2">c = GN&minus;WN = {{ northGraphicResults[coordinateInSystem.id].meridianConvergenceFormatted }}</div>
                  </div>

                  <div class="flex-fill"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="openForLocation" class="clearfix">
        <hr class="border-secondary my-2" />

        <span class="small" v-html="openForLocation.reactiveProps.description_preview_html"></span>

        <div class="float-end btn-group ms-1">
          <button
              type="button"
              class="btn btn-sm btn-outline-secondary pt-0 px-1 route-intermediates-mode-draw"
              title="Beschrijving"
              v-if="openForLocation.reactiveProps.description_preview_html_cut || openForLocation.reactiveProps.description_contains_markup"
              v-on:click="viewLocationDescription"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-journal-text" viewBox="0 0 16 16">
              <path d="M5 10.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5m0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5"/>
              <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2"/>
              <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1z"/>
            </svg>
          </button>

          <button
              type="button"
              class="btn btn-sm btn-outline-secondary pt-0 px-1 route-intermediates-mode-draw"
              title="Beschrijving"
              v-if="!userInterface.isLocked()"
              v-on:click="editLocationDescription"
          >
            <span class="small me-1" v-if="!openForLocation.reactiveProps.description_md">Beschrijving</span>

            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16">
              <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
              <path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import $ from "jquery";
import UserInterface from "../Main/UserInterface";
import OLConvertibleCoordinate from "../Coordinates/OLConvertibleCoordinate";
import OLConvertibleCoordinateSystem from "../Coordinates/OLConvertibleCoordinateSystem";
import CoordinateConverter from "../Util/CoordinateConverter";
import Coordinate from "../Coordinates/Coordinate";
import {copyInput, formatDeltaDegMin, formatDeltaDegrees, isMobile} from "../Util/functions";
import {Collection, Feature} from "ol";
import {Point} from "ol/geom";
import {Icon, Style} from "ol/style";
import mapMarker from '../img/marker.svg';
import Location from "../Main/Location";
import Container, {ElevationMode} from "../Main/Container";
import {Translate} from "ol/interaction";
import WGS84 from "../Coordinates/WGS84";
import Map from "../Main/Map";
import Route from "../Main/Route.js";
import {computed} from "vue";
import {fromLonLat} from "ol/proj";
import ElevationProvider from "../Main/ElevationProvider.js";
import MagneticNorthProvider, {GeoMagResponse} from "../Main/MagneticNorthProvider.js";
import MapNorthSvgGraphic from "../Util/MapNorthSvgGraphic.js";

export default {
  props: {
    userInterface: <UserInterface>null,
    olConvertibleCoordinateSystem: <OLConvertibleCoordinateSystem<OLConvertibleCoordinate>><unknown>Object,
  },
  data () {
    this.openForLocation = null;
    this.coordinate = <WGS84|null>null;
    this.coordinateName = null;
    this.currentFeature = null;
    this.markerTranslateInteraction = null;
    this.magneticNorthProvider = <MagneticNorthProvider|null>null;
    (async () => {
      this.magneticNorthProvider = await Container.getMagneticNorthProvider();
    })();

    const map: Map = this.userInterface.getMap();
    const openlayersMap = map.getOpenlayersMap();

    this.markerFeature = new Feature({
      geometry: new Point(openlayersMap.getView().getCenter()), // Initialize with dummy point
    });

    this.initializeMarkerInteraction();

    openlayersMap.on('singleclick', (e) => {
      if (this.userInterface.getRouteCollection().hasFocusedRoute() || this.userInterface.getShapeCollection().hasFocusedShape()) {
        // Very rarely on mobile (android/iphone) a event falls through to here while a route is active.
        // Make sure we do not mark a coordinate in these cases
        return;
      }

      if(this.currentFeature === null) {
        this.openCoordinatePanel(this.olConvertibleCoordinateSystem.fromOpenLayersCoordinate(
            openlayersMap.getCoordinateFromPixel(e.pixel)
        ));
      } else {
        this.closeCoordinatePanel();
      }
    });

    map.listen('geolocate:start', (data) => {
      this.geolocateActive = true;
      this.geolocateLonLat = data.lonlat;
    });
    map.listen('geolocate:stop', () => {
      this.geolocateActive = false;
      this.geolocateLonLat = null;
    });
    map.listen('geolocate:locate', (data) => {
      if (this.geolocateActive) {
        this.geolocateLonLat = data.lonlat;
      }
    });

    return {
      coordinatePanelRecomputeCounter: 0,
      geolocateActive: false,
      geolocateLonLat: null,
      elevation: null,
      awaitingElevationPromise: 0,
      magneticNorthData: <GeoMagResponse>null,
      northGraphicResults: {},
    };
  },
  watch: <Record<string, () => any>>{
    coordinatePanelRecomputeCounter: async function () {
      const longitude = this.wgs84Coordinate?.getX();
      const latitude = this.wgs84Coordinate?.getY();
      if (longitude !== null && latitude !== null) {
        this.magneticNorthData = this.magneticNorthProvider?.retrieve(longitude, latitude);
      } else {
        this.magneticNorthData = null;
      }

      const map: Map = this.userInterface.getMap();
      let elevationProvider: ElevationProvider = map.getElevationProvider();
      if ((elevationProvider === null && this.defaultElevationMode != ElevationMode.AlwaysOn) || this.coordinate === null) {
        this.elevation = null;
        return;
      }

      const coordinate: Coordinate|OLConvertibleCoordinate = this.coordinate;

      let elevation: number|null = null;
      this.awaitingElevationPromise++;
      try {
        if (this.defaultElevationMode == ElevationMode.AlwaysOn) {
          elevationProvider = await map.ensureElevationProvider();
        }
        elevation = await elevationProvider.getElevationAtLonLat([coordinate.getX(), coordinate.getY()]);
      } finally {
        this.awaitingElevationPromise--;
      }
      const formattedElevation = elevation !== null ? Route.formatMeters(elevation, 0) : '?';

      this.elevation = {
        elevation: elevation,
        formattedElevation: formattedElevation,
      };
    },
    coordinateInSystems: async function () {
      const minusChar = '−'; // &minus;

      const northGraphicResults = {};
      for (const coordinateInSystem of this.coordinateInSystems ?? []) {
        if (coordinateInSystem.coordinateSystem.code === 'EPSG:4326') { // TODO: Generalize this condition?
          continue;
        }

        try {
          const converted = coordinateInSystem.coordinate;

          const northGraphicResult = await MapNorthSvgGraphic.buildNorthSvgGraphic(converted, isMobile() ? 130 : 170);

          northGraphicResults[coordinateInSystem.id] = {
            svg_string: (new XMLSerializer()).serializeToString(northGraphicResult.svg),
            magneticDeclinationFormatted: formatDeltaDegMin(northGraphicResult.magneticDeclination).replace('-', minusChar),
            gridDeclinationFormatted: formatDeltaDegMin(northGraphicResult.gridDeclination).replace('-', minusChar),
            meridianConvergenceFormatted: formatDeltaDegMin(northGraphicResult.meridianConvergence).replace('-', minusChar),
          };
        } catch {
          // Ignore
        }
      }

      this.northGraphicResults = northGraphicResults;
    },
  },
  computed: {
    ElevationMode() {
      return ElevationMode;
    },
    coordinateInSystems: function() {
      this.coordinatePanelRecomputeCounter;
      this.userInterface.reactiveData.uiCoordinatePanelRecomputeCounter;

      if(this.currentFeature === null || this.coordinate === null) {
        return null;
      }

      const preferredFormats = Container.getPreferredCoordinateFormats();
      const coordinateSystemsInPanel = Container.getCoordinateSystemsInPanel().coordinateSystemsInPanel;

      coordinateSystemsInPanel.sort();

      const coordinateInSystems = [];
      for(const code of coordinateSystemsInPanel) {
        const coordinateSystem = CoordinateConverter.getCoordinateSystem(code);
        const converted = CoordinateConverter.convert(this.coordinate, coordinateSystem);
        const formats = converted.formats();
        const formatNames = Object.keys(formats);

        if(formatNames.length > 0) {
          let showFormatName;
          if(preferredFormats.hasOwnProperty(code) && formats.hasOwnProperty(preferredFormats[code])) {
            showFormatName = preferredFormats[code];
          } else {
            showFormatName = converted.defaultFormat();
          }

          const formatted: Record<string, string> = {};
          for(const formatName of formatNames) {
            formatted[formatName] = formats[formatName]();
          }

          coordinateInSystems.push({
            id: coordinateSystem.code.replace(/[^a-z0-9]/g, '_'),
            coordinate: converted,
            coordinateSystem: coordinateSystem,
            name: coordinateSystem.name,
            short_name: coordinateSystem.short_name,
            formatted: formatted,
            showFormatted: formatted[showFormatName],
          });
        }
      }

      return coordinateInSystems;
    },
    wgs84Coordinate: function (): WGS84 {
      this.coordinatePanelRecomputeCounter;
      this.userInterface.reactiveData.uiCoordinatePanelRecomputeCounter;

      if(this.currentFeature === null || this.coordinate === null) {
        return null;
      }

      const coordinateSystem = CoordinateConverter.getCoordinateSystem('EPSG:4326');
      return <WGS84>CoordinateConverter.convert(this.coordinate, coordinateSystem);
    },
    coordinateLinks: function() {
      const formatUnderscoreDms = (degrees: number, n: 'W'|'S', p: 'E'|'N') => {
        const postfix = degrees < 0 ? n : p;
        degrees = Math.abs(degrees);

        const deg = Math.floor(degrees);
        const minutes = (degrees - deg) * 60;
        const min = Math.floor(minutes);
        const sec = (minutes - min) * 60;

        return deg + '_' + min + '_' + Math.round(sec) + '_' + postfix;
      };

      const longitude = this.wgs84Coordinate?.getX();
      const latitude = this.wgs84Coordinate?.getY();
      const longitudeWE = longitude < 0 ? ((-longitude) + 'W') : (longitude + 'E');
      const latitudeNS = latitude < 0 ? ((-latitude) + 'S') : (latitude + 'N');

      return [
        {
          label: 'Plattekaart',
          url: 'https://plattekaart.nl?c=' + latitude + ',' + longitude,
        },
        {
          label: 'Google Maps',
          url: 'https://www.google.nl/maps/place/' + latitudeNS + '+' + longitudeWE,
        },
        {
          label: 'Google Street View',
          url: 'https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=' + latitude + ',' + longitude,
        },
        {
          label: 'Geohack',
          url: 'https://geohack.toolforge.org/geohack.php?language=nl&params=' + formatUnderscoreDms(latitude, 'S', 'N') + '_' + formatUnderscoreDms(longitude, 'W', 'E'),
        },
      ];
    },
    geolocateInfo: function () {
      this.coordinatePanelRecomputeCounter;

      if (this.geolocateLonLat === null || this.coordinate === null) {
        return null;
      }

      const from = fromLonLat(this.geolocateLonLat);
      const to = this.coordinate.toOpenLayersCoordinate();

      const bearing = Route.computeBearing(from, to);
      const formattedBearing = Route.formatBearing(bearing);
      const formattedCardinalDirection = computed(() => {
        return Container.getReactiveProps().cardinalDirections.bearingToDirection(bearing);
      });
      const formattedDistance = Route.formatLength(Route.computeDistance(from, to), 2, 0);

      return {
        bearing: bearing,
        formattedBearing: formattedBearing,
        formattedCardinalDirection: formattedCardinalDirection,
        formattedDistance: formattedDistance,
      };
    },
    defaultElevationMode: function () {
      this.coordinatePanelRecomputeCounter;

      return this.userInterface.getMap().defaultElevationMode;
    },
  },
  methods: {
    isMobile,
    copyInput,
    formatDeltaDegrees,
    initializeMarkerInteraction() {
      const openlayersMap = this.userInterface.getMap().getOpenlayersMap();

      this.markerTranslateInteraction = new Translate({
        features: new Collection([this.markerFeature]),
      });

      this.markerTranslateInteraction.on('translatestart', (e) => {
        document.getElementById('map-canvas').classList.add('coordinate-marker-dragging');
      });

      this.markerTranslateInteraction.on('translateend', (e) => {
        document.getElementById('map-canvas').classList.remove('coordinate-marker-dragging');
        this.updateLocationCoordinate();
      });

      openlayersMap.addInteraction(this.markerTranslateInteraction);

      openlayersMap.on('pointermove', (e) => {
        if (e.dragging || this.currentFeature !== this.markerFeature) {
          return;
        }

        if (this.userInterface.getRouteCollection().getRouTechSelectedRoute()?.getEditMode() !== 'intermediate') {
          for (const feature of openlayersMap.getFeaturesAtPixel(e.pixel)) {
            if (feature === this.markerFeature) {
              document.getElementById('map-canvas').classList.add('coordinate-marker-hover');
              return;
            }
          }
        }

        document.getElementById('map-canvas').classList.remove('coordinate-marker-hover');
      });
    },
    updateFormatDropdownWidth(event) {
      const $btn = $(event.target);
      $btn.siblings('.dropdown-menu').width($btn.closest('#coordinateControlPanelContent').width());
    },
    updateLocationCoordinate() {
      if (this.openForLocation) {
        this.coordinate = this.olConvertibleCoordinateSystem.fromOpenLayersCoordinate(this.openForLocation.getCoordinate());
      } else {
        this.coordinate = this.olConvertibleCoordinateSystem.fromOpenLayersCoordinate(this.markerFeature.getGeometry().getCoordinates());
      }
      this.coordinatePanelRecomputeCounter++;
      this.elevation = null;
    },
    toggleCoordinatePanelForLocation(location: Location) {
      const closeOnly = (this.openForLocation && this.openForLocation.id === location.id);

      this.closeCoordinatePanel();

      if (closeOnly) {
        return;
      }

      this.coordinate = this.olConvertibleCoordinateSystem.fromOpenLayersCoordinate(location.getCoordinate());
      this.coordinateName = null;

      location.on('change', this.updateLocationCoordinate);

      this.currentFeature = location.getFeature();
      this.openForLocation = location;
      this.coordinatePanelRecomputeCounter++;

      $('#coordinatePanel').toggleClass('d-none', false);
    },
    openCoordinatePanel(coordinate: Coordinate, coordinateName: string|null = null) {
      this.closeCoordinatePanel();

      const map = this.userInterface.getMap();

      this.coordinate = CoordinateConverter.convert(coordinate, this.olConvertibleCoordinateSystem);
      this.coordinateName = coordinateName;

      this.markerFeature.getGeometry().setCoordinates(this.coordinate.toOpenLayersCoordinate());
      this.currentFeature = this.markerFeature;

      this.openForLocation = null;
      this.coordinatePanelRecomputeCounter++;

      const downloaded = map.markerDownloaded();
      if (!downloaded) {
        this.markerFeature.setStyle(new Style({
          image: new Icon({
            anchor: [12, 41],
            anchorXUnits: 'pixels',
            anchorYUnits: 'pixels',
            scale : 0.75,
            opacity: 1,
            src: mapMarker
          }),
        }));
      }

      map.getCoordinatePanelMarker().then((icon) => {
        this.markerFeature.setStyle(new Style({
          image: icon,
        }));

        this.markerFeature.changed();
      });

      map.getOpenlayersVectorSource().addFeature(this.currentFeature);
      map.getOpenlayersVectorSource().changed();
      $('#coordinatePanel').toggleClass('d-none', false);
    },
    closeCoordinatePanel() {
      // The 'close' button is primarily provided for iOS devices, as these don't correctly close the
      // coordinate panel on a second click
      if (this.currentFeature === null) {
        return;
      }

      if (this.openForLocation === null) {
        this.userInterface.getMap().getOpenlayersVectorSource().removeFeature(this.currentFeature);
        document.getElementById('map-canvas').classList.remove('coordinate-marker-hover');
      } else {
        this.openForLocation.off('change', this.updateLocationCoordinate);
      }
      this.currentFeature = null;
      this.coordinate = null;
      this.coordinateName = null;
      this.openForLocation = null;
      this.coordinatePanelRecomputeCounter++;

      $('#coordinatePanel').toggleClass('d-none', true);
    },
    openSketchRoute() {
      if (this.currentFeature === null) {
        return;
      }

      const coordinate = this.currentFeature.getGeometry().getCoordinates();
      this.closeCoordinatePanel();
      this.userInterface.getRouteCollection().startSketchRoute(coordinate);
    },
    makeLocationFromCoordinate() {
      if (this.currentFeature === null) {
        return;
      }

      const coordinate = this.currentFeature.getGeometry().getCoordinates();
      const name = this.coordinateName;
      this.closeCoordinatePanel();
      this.userInterface.getLocationCollection().addLocation(coordinate, name);
    },
    copyCoordinateLink(coordinateLink) {
      navigator.clipboard.writeText(coordinateLink.url);
    },
    viewLocationDescription() {
      if (!this.openForLocation) {
        return;
      }

      this.userInterface.displayLocationDescriptionModal(this.openForLocation);
    },
    editLocationDescription() {
      if (!this.openForLocation) {
        return;
      }

      this.userInterface.editLocationDescription(this.openForLocation);
    },
    async enableElevation() {
      this.awaitingElevationPromise++;

      try {
        await this.userInterface.getMap().ensureElevationProvider(true);
      } finally {
        this.awaitingElevationPromise--;
      }

      this.coordinatePanelRecomputeCounter++;
    }
  }
};

</script>

<style scoped>
#coordinatePanel {
  padding: 15px 10px 10px 10px;
  min-width: calc(min(20rem, 100vw - 20px)); /* Fixed width or #coordinatePanelWrapper width */
}

#coordPanelGeolocate {
  float: left;
  margin-top: -8px;
}

.control-pane-coordinate-panel .btn-close {
  float: right;
  margin-top: -12px;
  margin-right: -6px;
}

.control-pane-coordinate-panel .btn-close svg {
  vertical-align: baseline;
}

.control-pane-coordinate-panel .btn-close.open-sketch-route, .control-pane-coordinate-panel .btn-close.open-coordinate-links {
  background: white;
  margin-right: 3px;
}

.control-pane-coordinate-panel .btn-close.make-location-from-coordinate {
  background: white;
  margin-top: -13px;
  margin-right: 4px;
}

.coordinate-link-item {
  padding: 0;
  display: flex;
  font-size: 80%;
}

.coordinate-link-item a, .coordinate-link-item a:hover {
  text-decoration: none;
  color: var(--bs-body-color);
  flex: 1;
  padding-left: 0.5rem;
  align-content: center;
}

.coordinate-link-item a:hover {
  background-color: rgba(0, 20, 100, 0.1);
}

.coordinate-link-item button {
  border-width: 0;
}

.coordinate-short-name-label {
  min-width: 4rem;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}
</style>
